// FlixPlayerNoSeekDetector.js
// Leaker detection component

import { throttle } from 'lodash';

/**
 * @typedef {import('video.js').VideoJsPlayer} VideoJsPlayer
 * @typedef {({noSeekDuration:number, messageDuration:number})=>void} OnDetectedCallback
 */

// TODO: remote config (start)
const minVideoDurationSeconds = 420;
const maxThredholdSeconds = 300;

const REGULAR_SEEK_DURATION_BIASED_MSEC = 200; // biased of avg seek time
const REGULAR_SEEK_DEBOUNCE_SEC = 15000 - REGULAR_SEEK_DURATION_BIASED_MSEC; // 15 sec (biased 200ms)
const REGULAR_SEEK_DURATION_LEAST_TIME = 2; // check with previous 2 time
// TODO: remote config (end)

export default class FlixPlayerNoSeekDetector {
  /** @type {VideoJsPlayer}*/
  player = null;
  /** @type {OnDetectedCallback} */
  onNoSeekDetected = null;
  onRegularSeekDetected = null;

  lastSeekedPosition = 0;
  duration = 0;

  isLongEnough = false;
  noSeekThreshold = maxThredholdSeconds;

  /**
   * @param {VideoJsPlayer} player
   * @param {OnDetectedCallback} onNoSeekDetected
   * @param {OnDetectedCallback} onRegularSeekDetected
   */
  static mount(player, onNoSeekDetected, onRegularSeekDetected) {
    const detector = new FlixPlayerNoSeekDetector(
      player,
      onNoSeekDetected,
      onRegularSeekDetected
    );
    detector.activate();
    return detector;
  }

  /**
   * @param {VideoJsPlayer} player
   * @param {OnDetectedCallback} onNoSeekDetected
   * @param {OnDetectedCallback} onRegularSeekDetected
   */
  constructor(player, onNoSeekDetected, onRegularSeekDetected) {
    this.player = player;
    this.onNoSeekDetected = onNoSeekDetected;
    this.onRegularSeekDetected = onRegularSeekDetected;
    this.seekedDurarionList = [];
    this.lastSeekedTime = null;
  }

  activate() {
    this.lastSeekedPosition = 0;
    this.player.on('loadedmetadata', this.handleLoadedMetaData);
    this.player.on('ended', this.handleEnded);
    this.player.on('seeked', this.handleSeeked);
    this.player.on('timeupdate', this.handleTimeUpdate);
  }

  deactivate() {
    this.player.off('loadedmetadata', this.handleLoadedMetaData);
    this.player.off('ended', this.handleEnded);
    this.player.off('seeked', this.handleSeeked);
    this.player.off('timeupdate', this.handleTimeUpdate);
  }

  getStandarDeviation = ({ list }) => {
    const sum = list.reduce((p, c) => p + c, 0);
    const avg = sum / list.length;
    const differenceSquare = list.reduce((p, c) => {
      const biased = c - avg;
      return biased * biased + p;
    }, 0);
    const standardDeviation = Math.sqrt(differenceSquare / list.length);

    return standardDeviation;
  };
  handleLoadedMetaData = () => {
    this.duration = this.player.duration();
    this.isLongEnough = this.duration > minVideoDurationSeconds;
    this.noSeekThreshold = Math.min(this.duration * 0.5, maxThredholdSeconds);
  };
  handleEnded = () => {
    // Reset on playlist next (etc. splash ended)
    this.lastSeekedTime = null;
    this.seekedDurarionList = [];
  };
  handleSeeked = () => {
    const current = this.player.currentTime();
    const now = Date.now();
    const durationOffset = this.lastSeekedTime ? now - this.lastSeekedTime : 0;
    if (durationOffset > REGULAR_SEEK_DEBOUNCE_SEC) {
      this.seekedDurarionList?.push(durationOffset);
    }

    this.lastSeekedTime = now;
    this.lastSeekedPosition = current;

    this.handleRegularSeekDetection();
  };
  handleRegularSeekDetection = async () => {
    const durationList = this.seekedDurarionList.slice(1);
    let durationStandardDeviation = null;
    if (durationList.length) {
      durationStandardDeviation = this.getStandarDeviation({
        list: durationList,
      });
      if (
        durationList.length >= REGULAR_SEEK_DURATION_LEAST_TIME &&
        durationStandardDeviation &&
        durationStandardDeviation < REGULAR_SEEK_DURATION_BIASED_MSEC
      ) {
        // Detect callback
        const avg =
          durationList.reduce((p, c) => p + c, 0) / durationList.length;
        this.onRegularSeekDetected?.({
          regularSeekDuration: avg,
          regularSeekCount: durationList.length,
          messageDuration: this.duration,
        });
      }
    }
  };
  handleTimeUpdate = throttle(() => {
    // We used to call deactivate() here when a specific condition meets.
    // Now it's auto-controlled.
    // (player.off() would be called in unbindListeners in ShakaPlayer)
    if (!this.player) return;
    if (
      this.isLongEnough &&
      this.lastSeekedPosition < 5 &&
      this.player.currentTime() - this.lastSeekedPosition > this.noSeekThreshold
    ) {
      this.onNoSeekDetected?.({
        noSeekDuration: this.player.currentTime() - this.lastSeekedPosition,
        messageDuration: this.duration,
      });
    }
  }, 1000); // TODO: remote config
}
